import { Pagination, Button, PageItemProps } from "react-bootstrap";
import getClasses from "utils/getClasses";
import getPageIcon from "./utils/getPageIcon";
import getRange from "./utils/getRange";

import styles from "./styles.module.scss";
import PAGE_ITEM_TYPES from "./constants/pageItemTypes";
import messages from "messages";
import { useMemo } from "react";

type Props = {
  onPageChange: (arg: number) => void;
  page: number;
  /**
   * total data size
   */
  dataCount: number;
  /**
   * data count per page
   * how many data should be displayed in one page
   */
  dataCountPerPage: number;
  boundaryCount?: number;
  disabled?: boolean;
  siblingCount?: number;
  className?: string;
  /**
   * align to the left, right, middle of the parent container
   * @default "middle"
   */
  align?: "left" | "right" | "middle";
};

/**
 * renders an advanced pagination which should show ellipsis when pages exceed certain limit
 * page starts with 1
 */
const AppPagination = ({
  page,
  onPageChange,
  boundaryCount = 1,
  dataCount,
  dataCountPerPage,
  disabled = false,
  siblingCount = 1,
  className,
  align = "middle",
}: Props) => {
  const pageCount = useMemo(
    () => Math.ceil(dataCount / dataCountPerPage),
    [dataCount, dataCountPerPage],
  );
  const startPages = getRange(1, Math.min(boundaryCount, pageCount));
  const endPages = getRange(
    Math.max(pageCount - boundaryCount + 1, boundaryCount + 1),
    pageCount,
  );

  const siblingsStart = Math.max(
    Math.min(
      // Natural start
      page - siblingCount,
      // Lower boundary when page is high
      pageCount - boundaryCount - siblingCount * 2 - 1,
    ),
    // Greater than startPages
    boundaryCount + 2,
  );

  const siblingsEnd = Math.min(
    Math.max(
      // Natural end
      page + siblingCount,
      // Upper boundary when page is low
      boundaryCount + siblingCount * 2 + 2,
    ),
    // Less than endPages
    endPages.length > 0 ? endPages[0] - 2 : pageCount - 1,
  );

  // Basic list of items to render
  // e.g. itemList = ['first', 'previous', 1, 'ellipsis', 4, 5, 6, 'ellipsis', 10, 'next', 'last']
  const itemList: (PageItemProps & {
    type?: keyof typeof PAGE_ITEM_TYPES;
  })[] = [
    // hide first
    // ...(page > 1 ? [PAGE_ITEM_TYPES.first] : []),
    ...(page > 1 ? [PAGE_ITEM_TYPES.previous] : []),
    ...startPages,

    // Start ellipsis
    ...(siblingsStart > boundaryCount + 2
      ? [PAGE_ITEM_TYPES.ellipsis]
      : boundaryCount + 1 < pageCount - boundaryCount
      ? [boundaryCount + 1]
      : []),

    // Sibling pages
    ...getRange(siblingsStart, siblingsEnd),

    // End ellipsis
    ...(siblingsEnd < pageCount - boundaryCount - 1
      ? [PAGE_ITEM_TYPES.ellipsis]
      : pageCount - boundaryCount > boundaryCount
      ? [pageCount - boundaryCount]
      : []),

    ...endPages,
    ...(page < pageCount ? [PAGE_ITEM_TYPES.next] : []),
    // remove the comment if need to hide last
    // ...(page < pageCount ? [PAGE_ITEM_TYPES.last] : []),
  ]
    // Convert the basic item list to PaginationItem props objects
    .map((item) => {
      return typeof item === "number"
        ? {
            onClick: () => {
              onPageChange(item);
            },
            disabled: disabled,
            active: item === page,
            children: item,
            "aria-current": item === page ? "true" : undefined,
          }
        : {
            onClick: () => {
              const nextPage = getNextPage(item);
              if (nextPage >= 0) {
                onPageChange(nextPage);
              }
            },
            type: item,
            active: false,
            disabled:
              disabled ||
              item === PAGE_ITEM_TYPES.ellipsis ||
              item === PAGE_ITEM_TYPES.next
                ? // remove the comment if need to hide last
                  // || (item === PAGE_ITEM_TYPES.last)
                  page >= pageCount
                : page <= 1,
            children: getPageIcon(item),
          };
    });

  // Map the button type to its page number
  const getNextPage = (type: keyof typeof PAGE_ITEM_TYPES) => {
    switch (type) {
      case PAGE_ITEM_TYPES.first:
        return 1;
      case PAGE_ITEM_TYPES.previous:
        return page - 1;
      case PAGE_ITEM_TYPES.next:
        return page + 1;
      case PAGE_ITEM_TYPES.last:
        return pageCount;
      default:
        return -1;
    }
  };

  return pageCount <= 0 ? null : (
    <div
      className={getClasses(
        styles["app-pagination"],
        styles[`app-pagination--${align}`],
        className,
      )}
    >
      <Pagination
        as={"div"}
        className={getClasses(styles["app-pagination__pager"], className)}
      >
        {itemList.map(({ type, ...item }, index) => {
          if (!type) {
            return (
              <Button
                variant={"link"}
                key={index}
                className={styles["app-pagination__page-item"]}
                {...item}
              />
            );
          }

          return (
            <Button
              variant={"link"}
              key={index}
              className={getClasses(
                styles["app-pagination__page-item"],
                styles["app-pagination__page-item--icon"],
                type !== PAGE_ITEM_TYPES.ellipsis &&
                  styles["app-pagination__page-item--icon--action"],
              )}
              {...item}
            />
          );
        })}
      </Pagination>
      <div className={styles["app-pagination__info"]}>
        {`${(page - 1) * dataCountPerPage + 1}-${Math.min(
          page * dataCountPerPage,
          dataCount,
        )} ${messages.of} ${dataCount}`}
      </div>
    </div>
  );
};

export default AppPagination;
